// VOID Card Warfare — Options Fullscreen Modal
//
// Fullscreen dialog for Audio, Graphics Quality, Performance HUD, and Legal settings.
// Styled with background.webp and Startscreen + Arsenal > Progress sci-fi aesthetic.

import { showLegalModal } from './legalModal.js';
import { showWingmanModal } from './wingmanModal.js';
import { AB_FLAGS, isAbOn, setAbFlag } from './abFlags.js';
import { aiOpponentMemory } from '../engine/ai/opponentMemory.js';
import { aiAdaptiveMemory } from '../engine/ai/adaptiveMemory.js';
import { isDevToolsOn, setDevToolsOn, createTapTrigger } from './devTools.js';
import { getFormattedBuildTag, applyUpdateAndReload } from './updateManager.js';

let activeOverlay = null;

export function showOptionsFullscreenModal(game) {
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
    game?.overlayGate?.release?.();
  }
  game?.overlayGate?.acquire?.();

  const audio = game?.audio;
  const perf = game?.performanceManager;

  const currentSfx = Math.round((audio?.getSfxVolume?.() ?? audio?.sfxVolume ?? 0.6) * 100);
  const currentMusic = Math.round((audio?.getMusicVolume?.() ?? audio?.musicVolume ?? 0.7) * 100);
  const currentQuality = perf?.manual || 'auto';
  const isPerfHudOn = perf?.isOverlayEnabled?.() ?? false;
  const isEcoMode = game?.isEcoMode ?? false;
  const currentAIDifficulty = game?.currentAIDifficulty || 'medium';
  const isAiHandVisible = game?.showAiHand ?? false;
  // Memory probe (js/ui/memProbe.js): off by default, overlay shown on demand. Absent unless the
  // recording was switched on with ?probe=on.
  const memProbe = window.__memProbe || null;
  const memHudLabel = (on) => `🧠 MEMORY PROBE: ${on ? 'SHOWN' : 'HIDDEN'}`;
  const isMemHudOn = memProbe?.hudVisible?.() ?? false;
  const abLabel = (flag, on) => `🧪 A/B ${AB_FLAGS[flag]}: ${on ? 'ON' : 'OFF'}`;
  // The two developer blocks below stay hidden until the header is tapped 3x (js/ui/devTools.js).
  let devStorage = null;
  try { devStorage = window.localStorage; } catch { devStorage = null; }
  const isDevOn = isDevToolsOn(devStorage);

  const layer = document.createElement('div');
  layer.id = 'options-fullscreen-modal';
  layer.className = 'fullscreen-dialog-overlay options-overlay';

  layer.innerHTML = `
    <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
      <div class="fullscreen-dialog-bg-image"></div>
      <div class="fullscreen-dialog-scrim"></div>
      <div class="fullscreen-dialog-vignette"></div>
    </div>

    <div class="fullscreen-dialog-container">
      <header class="fullscreen-dialog-header">
        <div class="fullscreen-dialog-header-left" id="options-header-left" style="touch-action: manipulation; -webkit-user-select: none; user-select: none;">
          <div class="fullscreen-dialog-category" id="options-category">${isDevOn ? 'SYSTEM · DEV' : 'SYSTEM'}</div>
          <h2 class="fullscreen-dialog-title">OPTIONS</h2>
        </div>
        <div class="fullscreen-dialog-header-actions">
          <div class="fullscreen-dialog-status-pill status-pill-circular" title="System Nominal">
            <span class="status-indicator-dot"></span>
          </div>
          <button type="button" class="modal-close-icon-btn" id="options-header-close-btn" aria-label="Close Options" title="Close (Esc)">
            <svg class="dialog-close-svg" viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="2.5" y1="2.5" x2="11.5" y2="11.5" />
              <line x1="11.5" y1="2.5" x2="2.5" y2="11.5" />
            </svg>
          </button>
        </div>
      </header>

      <div class="fullscreen-dialog-body">
        <div class="options-dialog-columns">
          <!-- Panel 1: Audio -->
          <div class="fullscreen-card-panel">
            <div class="fullscreen-card-header">
              <span class="fullscreen-card-title">AUDIO CHANNELS</span>
              <span class="stat-label" style="color: #00f0ff; font-family: 'IBM Plex Mono', monospace;">STEREO 2.0</span>
            </div>

            <div class="options-controls-list" style="margin-top: 16px;">
              <div id="options-slider-sfx" class="options-hud-slider" role="slider" aria-label="Sound Effects Volume" tabindex="0">
                <div class="options-hud-slider-fill" id="sfx-slider-fill" style="transform: translate3d(${currentSfx - 100}%, 0, 0);"></div>
                <div class="options-hud-slider-label">
                  <span>SOUND EFFECTS (SFX)</span>
                  <span class="options-hud-slider-val" id="sfx-val-label">${currentSfx}%</span>
                </div>
              </div>

              <div id="options-slider-music" class="options-hud-slider" role="slider" aria-label="Music Volume" tabindex="0">
                <div class="options-hud-slider-fill" id="music-slider-fill" style="transform: translate3d(${currentMusic - 100}%, 0, 0);"></div>
                <div class="options-hud-slider-label">
                  <span>MUSIC & AMBIENCE</span>
                  <span class="options-hud-slider-val" id="music-val-label">${currentMusic}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Panel 2: Graphics & HUD -->
          <div class="fullscreen-card-panel">
            <div class="fullscreen-card-header">
              <span class="fullscreen-card-title">GRAPHIC & MORE</span>
              <span class="stat-label" id="options-build-tag-label" style="color: #10b981; font-family: 'IBM Plex Mono', monospace;">${getFormattedBuildTag()}</span>
            </div>

            <div class="options-controls-list" style="margin-top: 16px;">
              <div class="options-preset-grid" id="options-quality-buttons">
                <button type="button" class="options-preset-btn ${currentQuality === 'auto' ? 'active' : ''}" data-quality="auto">AUTO</button>
                <button type="button" class="options-preset-btn ${currentQuality === 'ultra' ? 'active' : ''}" data-quality="ultra">ULTRA</button>
                <button type="button" class="options-preset-btn ${currentQuality === 'high' ? 'active' : ''}" data-quality="high">HIGH</button>
                <button type="button" class="options-preset-btn ${currentQuality === 'medium' ? 'active' : ''}" data-quality="medium">MEDIUM</button>
                <button type="button" class="options-preset-btn ${currentQuality === 'low' ? 'active' : ''}" data-quality="low">LOW</button>
                <button type="button" class="options-preset-btn options-eco-btn ${isEcoMode ? 'active' : ''}" id="options-toggle-eco" title="Render Refresh Rate: 60Hz / 30Hz">
                  ${isEcoMode ? '30HZ' : '60HZ'}
                </button>
              </div>

              <button type="button" id="options-toggle-perf" class="start-btn ${isPerfHudOn ? 'start-btn-primary' : 'start-btn-secondary'}" style="margin-top: 8px;">
                <span class="start-btn-text">${isPerfHudOn ? 'PERF HUD: ACTIVE' : 'PERF HUD: DISABLED'}</span>
              </button>

              <button type="button" id="options-btn-wingman" class="start-btn start-btn-gold" style="margin-top: 8px;" title="Generate single-use Closed Alpha clearance for a friend">
                <span class="start-btn-text">RECRUIT A WINGMAN</span>
              </button>

              <button type="button" id="options-btn-discord" class="start-btn start-btn-secondary" style="margin-top: 8px;" title="Join the VOID Conflict Discord to report bugs and transmit intel">
                <span class="start-btn-text">REPORT A BUG</span>
              </button>

              <button type="button" id="options-btn-check-update" class="start-btn start-btn-secondary" style="margin-top: 8px;" title="Synchronize cloud save, refresh app shell cache, and reload latest version">
                <span class="start-btn-text">UPDATE & RELOAD APP</span>
              </button>

              <div id="options-dev-tools" ${isDevOn ? '' : 'hidden'}>
              ${memProbe ? `<button type="button" id="options-toggle-memprobe" class="start-btn ${isMemHudOn ? 'start-btn-primary' : 'start-btn-secondary'}" style="margin-top: 8px;">
                <span class="start-btn-text">${memHudLabel(isMemHudOn)}</span>
              </button>` : ''}

              ${Object.keys(AB_FLAGS).map((flag) => `<button type="button" class="start-btn options-ab-toggle ${isAbOn(flag) ? 'start-btn-primary' : 'start-btn-secondary'}" data-ab-flag="${flag}" style="margin-top: 8px;">
                <span class="start-btn-text">${abLabel(flag, isAbOn(flag))}</span>
              </button>`).join('')}
              </div>
            </div>
          </div>

          <!-- Panel 3: AI & Legal -->
          <div class="fullscreen-card-panel">
            <div class="fullscreen-card-header">
              <span class="fullscreen-card-title">AI & SIMULATION</span>
              <span class="stat-label" style="color: #f59e0b; font-family: 'IBM Plex Mono', monospace;">OFFLINE BOT</span>
            </div>

            <div class="options-controls-list" style="margin-top: 16px;">
              <div class="options-preset-grid" id="options-ai-difficulty-buttons">
                <button type="button" class="options-preset-btn ${currentAIDifficulty === 'easy' ? 'active' : ''}" data-difficulty="easy" title="AI: Easy (naive/slow)">EASY</button>
                <button type="button" class="options-preset-btn ${currentAIDifficulty === 'medium' ? 'active' : ''}" data-difficulty="medium" title="AI: Normal (default)">NORMAL</button>
                <button type="button" class="options-preset-btn ${currentAIDifficulty === 'hard' ? 'active' : ''}" data-difficulty="hard" title="AI: Hard (advanced)">HARD</button>
                <button type="button" class="options-preset-btn ${currentAIDifficulty === 'veryHard' ? 'active' : ''}" data-difficulty="veryHard" title="The Very Hard AI receives +5% plasma income">
                  <span style="display: flex; flex-direction: column; align-items: center; line-height: 1.1; font-size: 11px;"><span>V.HARD</span><span style="font-size: 9.5px; color: var(--accent-green, #00ff88); opacity: 0.9;">+5%</span></span>
                </button>
                <button type="button" class="options-preset-btn ${currentAIDifficulty === 'insane' ? 'active' : ''}" data-difficulty="insane" title="The Insane AI receives +10% plasma income">
                  <span style="display: flex; flex-direction: column; align-items: center; line-height: 1.1; font-size: 11px;"><span>INSANE</span><span style="font-size: 9.5px; color: var(--accent-green, #00ff88); opacity: 0.9;">+10%</span></span>
                </button>
                <button type="button" class="options-preset-btn ${currentAIDifficulty === 'invincible' ? 'active' : ''}" data-difficulty="invincible" title="The Invincible AI receives +20% plasma income">
                  <span style="display: flex; flex-direction: column; align-items: center; line-height: 1.1; font-size: 11px;"><span>INVINCIBLE</span><span style="font-size: 9.5px; color: var(--accent-green, #00ff88); opacity: 0.9;">+20%</span></span>
                </button>
              </div>

              <button type="button" id="options-btn-reset-ai-memory" class="start-btn start-btn-secondary" style="margin-top: 8px;" title="The offline bot remembers your decks and habits on this device only. Nothing is uploaded.">
                <span class="start-btn-text">RESET AI MEMORY</span>
              </button>

              <button type="button" id="options-toggle-ai-hand" class="start-btn ${isAiHandVisible ? 'start-btn-primary' : 'start-btn-secondary'}" style="margin-top: 8px;" title="Displays the opponent AI's hand in the top right during AI vs AI (Exhibition) matches only.">
                <span class="start-btn-text">${isAiHandVisible ? 'EXHIBITION AI HAND: VISIBLE' : 'EXHIBITION AI HAND: HIDDEN'}</span>
              </button>

              <button type="button" id="options-btn-legal" class="start-btn start-btn-secondary" style="margin-top: 8px;">
                <span class="start-btn-text">LEGAL & PRIVACY</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Centered Bottom Action Button -->
      <footer class="fullscreen-dialog-footer">
        <button type="button" class="fullscreen-return-btn" id="options-return-btn">
          <span class="start-btn-text">RETURN</span>
        </button>
      </footer>
    </div>
  `;

  document.body.appendChild(layer);
  activeOverlay = layer;

  // Close handlers
  const closeModal = () => {
    audio?.playUiClick?.();
    layer.remove();
    activeOverlay = null;
    game?.overlayGate?.release?.();
    window.removeEventListener('keydown', handleKey);
  };

  const returnBtn = layer.querySelector('#options-return-btn');
  returnBtn?.addEventListener('click', closeModal);
  const headerCloseBtn = layer.querySelector('#options-header-close-btn');
  headerCloseBtn?.addEventListener('click', closeModal);

  const handleKey = (e) => {
    if (e.key === 'Escape') closeModal();
  };
  window.addEventListener('keydown', handleKey);

  // Setup interactive slider helper
  const setupSlider = (sliderId, fillId, labelId, getVolume, setVolume, onCommit) => {
    const sliderEl = layer.querySelector(sliderId);
    const fillEl = layer.querySelector(fillId);
    const labelEl = layer.querySelector(labelId);
    if (!sliderEl || !fillEl || !labelEl) return;

    let isDragging = false;
    let cachedRect = null;

    const applyRatio = (ratio, persist = false) => {
      const clamped = Math.max(0, Math.min(1, ratio));
      const pct = Math.round(clamped * 100);
      fillEl.style.transform = `translate3d(${pct - 100}%, 0, 0)`;
      labelEl.textContent = `${pct}%`;
      setVolume(clamped, persist);
    };

    const handleCoord = (clientX, persist = false) => {
      if (!cachedRect || cachedRect.width <= 0) {
        cachedRect = sliderEl.getBoundingClientRect();
      }
      if (cachedRect.width <= 0) return;
      const ratio = (clientX - cachedRect.left) / cachedRect.width;
      applyRatio(ratio, persist);
    };

    sliderEl.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      isDragging = true;
      sliderEl.classList.add('is-dragging');
      try { sliderEl.setPointerCapture(e.pointerId); } catch {}
      cachedRect = sliderEl.getBoundingClientRect();
      handleCoord(e.clientX, false);
    });

    sliderEl.addEventListener('pointermove', (e) => {
      if (!isDragging) return;
      handleCoord(e.clientX, false);
    });

    const endDrag = (e) => {
      if (!isDragging) return;
      isDragging = false;
      sliderEl.classList.remove('is-dragging');
      try { sliderEl.releasePointerCapture(e.pointerId); } catch {}
      if (e.clientX !== undefined) handleCoord(e.clientX, true);
      if (onCommit) onCommit();
    };

    sliderEl.addEventListener('pointerup', endDrag);
    sliderEl.addEventListener('pointercancel', endDrag);

    sliderEl.addEventListener('keydown', (e) => {
      let cur = getVolume();
      let handled = true;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') cur = Math.max(0, cur - 0.05);
      else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') cur = Math.min(1, cur + 0.05);
      else if (e.key === 'Home') cur = 0;
      else if (e.key === 'End') cur = 1;
      else handled = false;

      if (handled) {
        e.preventDefault();
        applyRatio(cur, true);
        if (onCommit) onCommit();
      }
    });

    sliderEl.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.05 : -0.05;
      const cur = Math.max(0, Math.min(1, getVolume() + delta));
      applyRatio(cur, true);
      if (onCommit) onCommit();
    }, { passive: false });
  };

  // SFX Slider
  setupSlider(
    '#options-slider-sfx',
    '#sfx-slider-fill',
    '#sfx-val-label',
    () => (audio?.getSfxVolume ? audio.getSfxVolume() : (audio?.sfxVolume ?? 0.6)),
    (vol, persist) => {
      audio?.setSfxVolume?.(vol, persist);
      const hudSfxFill = document.querySelector('#slider-volume-sfx .hud-slider-fill');
      if (hudSfxFill) hudSfxFill.style.transform = `translate3d(${Math.round(vol * 100) - 100}%, 0, 0)`;
    },
    () => audio?.playUiClick?.()
  );

  // Music Slider
  setupSlider(
    '#options-slider-music',
    '#music-slider-fill',
    '#music-val-label',
    () => (audio?.getMusicVolume ? audio.getMusicVolume() : (audio?.musicVolume ?? 0.7)),
    (vol, persist) => {
      audio?.setMusicVolume?.(vol, persist);
      const hudMusicFill = document.querySelector('#slider-volume-music .hud-slider-fill');
      if (hudMusicFill) hudMusicFill.style.transform = `translate3d(${Math.round(vol * 100) - 100}%, 0, 0)`;
    }
  );

  // Graphics Quality Preset Buttons
  const qualityBtns = layer.querySelectorAll('.options-preset-btn[data-quality]');
  qualityBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const q = btn.dataset.quality;
      qualityBtns.forEach((b) => b.classList.toggle('active', b === btn));
      perf?.setManual?.(q === 'auto' ? null : q);
      audio?.playUiClick?.();
      const hudSel = document.getElementById('select-quality');
      if (hudSel) hudSel.value = q;
    });
  });

  // Eco / Refresh Rate Toggle (60Hz vs 30Hz)
  const ecoBtn = layer.querySelector('#options-toggle-eco');
  ecoBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    const enabled = game?.setEcoMode?.(!game.isEcoMode) ?? false;
    if (ecoBtn) {
      ecoBtn.textContent = enabled ? '30HZ' : '60HZ';
      ecoBtn.classList.toggle('active', enabled);
    }
  });

  // Perf HUD Toggle
  const perfBtn = layer.querySelector('#options-toggle-perf');
  perfBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    const enabled = perf?.toggleOverlay?.() ?? false;
    if (perfBtn) {
      perfBtn.querySelector('.start-btn-text').textContent = enabled ? 'PERF HUD: ACTIVE' : 'PERF HUD: DISABLED';
      perfBtn.className = `start-btn ${enabled ? 'start-btn-primary' : 'start-btn-secondary'}`;
    }
    const hudPerfBtn = document.getElementById('btn-toggle-perf-overlay');
    if (hudPerfBtn) {
      hudPerfBtn.innerText = enabled ? 'Perf HUD: ON' : 'Perf HUD: OFF';
      hudPerfBtn.classList.toggle('active', enabled);
    }
  });

  // Hidden developer menu: 3 quick taps on the header show/hide the memory-probe and A/B buttons.
  const devBlock = layer.querySelector('#options-dev-tools');
  const devCategory = layer.querySelector('#options-category');
  const devTap = createTapTrigger({
    onTrigger: () => {
      const on = setDevToolsOn(devStorage, !isDevToolsOn(devStorage));
      if (devBlock) devBlock.hidden = !on;
      if (devCategory) devCategory.textContent = on ? 'SYSTEM · DEV' : 'SYSTEM';
      // A probe overlay must not stay on screen once its switch is hidden.
      if (!on) window.__memProbe?.setHudVisible?.(false);
      const memToggle = layer.querySelector('#options-toggle-memprobe');
      if (memToggle) {
        memToggle.querySelector('.start-btn-text').textContent = memHudLabel(false);
        memToggle.className = 'start-btn start-btn-secondary';
      }
    },
  });
  layer.querySelector('#options-header-left')?.addEventListener('click', () => devTap());

  // Memory Probe overlay toggle
  const memBtn = layer.querySelector('#options-toggle-memprobe');
  memBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    const shown = window.__memProbe?.setHudVisible?.(!window.__memProbe.hudVisible()) ?? false;
    memBtn.querySelector('.start-btn-text').textContent = memHudLabel(shown);
    memBtn.className = `start-btn ${shown ? 'start-btn-primary' : 'start-btn-secondary'}`;
  });

  // Developer A/B switches (js/ui/abFlags.js)
  layer.querySelectorAll('.options-ab-toggle').forEach((btn) => {
    btn.addEventListener('click', () => {
      audio?.playUiClick?.();
      const flag = btn.dataset.abFlag;
      const on = setAbFlag(flag, !isAbOn(flag));
      btn.querySelector('.start-btn-text').textContent = abLabel(flag, on);
      btn.className = `start-btn options-ab-toggle ${on ? 'start-btn-primary' : 'start-btn-secondary'}`;
    });
  });

  // AI Difficulty Preset Buttons
  const diffBtns = layer.querySelectorAll('#options-ai-difficulty-buttons .options-preset-btn[data-difficulty]');
  diffBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      audio?.playUiClick?.();
      const diff = btn.dataset.difficulty;
      diffBtns.forEach((b) => b.classList.toggle('active', b === btn));
      game?.setAIDifficulty?.(diff);
    });
  });

  // Reset AI Memory: the offline bot forgets this player (two clicks, no dialog).
  const resetMemoryBtn = layer.querySelector('#options-btn-reset-ai-memory');
  const resetMemoryLabel = resetMemoryBtn?.querySelector('.start-btn-text');
  let resetMemoryArmed = null;
  resetMemoryBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    if (resetMemoryArmed === null) {
      if (resetMemoryLabel) resetMemoryLabel.textContent = 'CLICK AGAIN TO CONFIRM';
      resetMemoryArmed = setTimeout(() => {
        resetMemoryArmed = null;
        if (resetMemoryLabel) resetMemoryLabel.textContent = 'RESET AI MEMORY';
      }, 4000);
      return;
    }
    clearTimeout(resetMemoryArmed);
    resetMemoryArmed = null;
    aiOpponentMemory.reset();
    aiAdaptiveMemory.reset();
    if (resetMemoryLabel) resetMemoryLabel.textContent = 'AI MEMORY CLEARED';
    setTimeout(() => { if (resetMemoryLabel && resetMemoryArmed === null) resetMemoryLabel.textContent = 'RESET AI MEMORY'; }, 2500);
  });

  // Toggle AI Hand Intel: reveals the bot's hand during AI vs AI (Exhibition) matches only
  const aiHandBtn = layer.querySelector('#options-toggle-ai-hand');
  const aiHandLabel = aiHandBtn?.querySelector('.start-btn-text');
  aiHandBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    const isVisible = game?.toggleShowAiHand?.() ?? false;
    aiHandBtn.className = `start-btn ${isVisible ? 'start-btn-primary' : 'start-btn-secondary'}`;
    aiHandBtn.style.marginTop = '8px';
    if (aiHandLabel) {
      aiHandLabel.textContent = isVisible ? 'EXHIBITION AI HAND: VISIBLE' : 'EXHIBITION AI HAND: HIDDEN';
    }
    const hudAiHandBtn = document.getElementById('btn-toggle-ai-hand');
    if (hudAiHandBtn) {
      hudAiHandBtn.innerText = isVisible ? 'AI Hand: ON' : 'AI Hand: OFF';
      hudAiHandBtn.classList.toggle('active', isVisible);
    }
  });

  // Recruit a Wingman: Generates or retrieves 1 one-shot Closed Alpha clearance key for a friend
  const wingmanBtn = layer.querySelector('#options-btn-wingman');
  wingmanBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    showWingmanModal(game);
  });

  // Report a Bug / Community via Discord
  const discordBtn = layer.querySelector('#options-btn-discord');
  discordBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    window.open('https://discord.gg/UECTSvDB4u', '_blank', 'noopener,noreferrer');
  });

  // Update & Reload App (tester & player 1-click shell cache refresh + cloud save sync)
  const updateBtn = layer.querySelector('#options-btn-check-update');
  updateBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    const label = updateBtn.querySelector('.start-btn-text');
    if (label) label.textContent = 'SYNCHRONIZING & RELOADING...';
    applyUpdateAndReload({ force: true, clearShellCache: true });
  });

  // Legal Notice
  const legalBtn = layer.querySelector('#options-btn-legal');
  legalBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    showLegalModal('imprint', game);
  });
}
